import { Controller } from '@hotwired/stimulus'
import 'dhtmlx-scheduler/codebase/dhtmlxscheduler.css'
import scheduler from 'dhtmlx-scheduler'
import i18next from 'i18next'

import { addNotification } from '../../helpers/notificationHelper'
import { DEFAULT_MIN_DATE_ISO, getCalendarData } from '../../services/sites/calendarTasksIndex'
import { serializeCalendarData, serializeEvent, serializeProviderEvent } from '../../services/sites/calendarService'
import { updateSiteTask } from '../../services/sites/calendarTaskService'
import { getProviderEvents } from '../../services/sites/providerEventsService'
import { percentageTemplateFormBlock, renderSites } from './calendar_utils/calendarUtils'
import { collapseAll as collapseAllUtils, expandToCategories as expandToCategoriesUtils, expandAll as expandAllUtils, setExpandCollapseActiveButton } from './calendar_utils/calendarExpandCollapseUtils'

// Connects to data-controller="sites--calendar"
export default class extends Controller {
  static targets = ['calendarObj', 'calendarTaskLister']
  static values = {
    readonly: Boolean,
    organizationId: String,
    projectId: String,
    importEnabled: Boolean,
  }

  connect() {
    this.siteTasks = []
    this.providerEvents = []
    this.visibleLayers = new Set(['site_task'])
    if (this.importEnabledValue) this.visibleLayers.add('provider_event')
    this.setupScheduler()
    this.loadCalendarData()
    if (this.importEnabledValue) this.loadProviderEvents()
  }

  disconnect() {
    scheduler.clearAll()
  }

  setupScheduler() {
    scheduler.i18n.setLocale(window.appData.locale)
    scheduler.config.dblclick_create = false
    scheduler.config.drag_create = false
    scheduler.locale.labels.section_percentage_completed = 'Percentuale completata'
    scheduler.config.buttons_left = []
    // scheduler.config.buttons_right = [] // For now don't support "delete" button
    scheduler.config.lightbox.sections = [
      { name: 'description', height: 50, map_to: 'text', type: 'textarea' },
      { name: 'time', height: 72, type: 'time', parse: 'HH:mm', map_to: 'auto', focus: true },
      { name: 'percentage_completed', height: 30, map_to: 'percentage_completed', type: 'percentage_template' },
    ]
    scheduler.form_blocks['percentage_template'] = percentageTemplateFormBlock
    scheduler.init(this.calendarObjTarget, new Date(), 'month')

    this.calendarObjTarget.ondrop = (event) => this.onDrop(event)
    this.calendarObjTarget.ondragover = (event) => event.preventDefault()

    // Layer filtering
    const controller = this
    scheduler.filter_month = scheduler.filter_week = scheduler.filter_day = function (id, event) {
      return controller.visibleLayers.has(event.type || 'site_task')
    }

    scheduler.attachEvent('onBeforeDrag', (id, mode, e) => {
      const event = scheduler.getEvent(id)
      return !event.readonly
    })
    scheduler.attachEvent('onBeforeEventChanged', (event, e, is_new, original) => {
      return !event.readonly
    })
    scheduler.attachEvent('onBeforeLightbox', function(id) {
      const event = scheduler.getEvent(id)
      return !event.readonly
    })
    scheduler.attachEvent('onLightbox', function () {
      const textarea = document.querySelector('.dhx_cal_ltext textarea')
      if (textarea) textarea.setAttribute('readonly', true)
    })
    scheduler.attachEvent('onEventChanged', (id, event) => {
      const payload = {
        start_date: event.start_date,
        end_date: event.end_date,
        percentage_completed: event.percentage_completed,
      }
      this.saveSiteTask(id, payload)
    })
    scheduler.plugins({
      minical: true,
    })
  }

  async loadCalendarData() {
    this.remoteData = await getCalendarData(this.organizationIdValue, this.projectIdValue, DEFAULT_MIN_DATE_ISO)
    const { siteTasks, projectTaskList } = serializeCalendarData(this.remoteData)
    this.updateCalendarTaskList(projectTaskList)
    this.initializeCollapseState()
    this.siteTasks = siteTasks.filter((site_task) => site_task.start_date && site_task.end_date)
    scheduler.setCurrentView(this.remoteData.settings.minDate)
    this.updateSchedulerEvents()
  }

  async loadProviderEvents() {
    const response = await getProviderEvents()
    this.providerEvents = (response.provider_events || []).map(serializeProviderEvent)
    this.updateSchedulerEvents()
  }

  updateSchedulerEvents() {
    scheduler.clearAll()
    scheduler.parse([...this.providerEvents, ...this.siteTasks], 'json')
  }

  toggleLayer(event) {
    const layer = event.target.dataset.layer
    if (event.target.checked) {
      this.visibleLayers.add(layer)
    } else {
      this.visibleLayers.delete(layer)
    }
    scheduler.updateView()
  }

  onDrop(event) {
    const siteTaskId = event.dataTransfer.getData('text/plain')
    if (!siteTaskId) return

    const siteTask = this.remoteData.calendarTasks.find(task => task.id === siteTaskId)
    const newStartDate = scheduler.getActionData(event).date

    if (siteTask.end_date) {
      const originalStartDate = new Date(siteTask.start_date)
      const originalEndDate = new Date(siteTask.end_date)
      const dateDiff = originalEndDate.getTime() - originalStartDate.getTime()
      siteTask.end_date = new Date(newStartDate.getTime() + dateDiff)
    } else {
      siteTask.end_date = newStartDate
    }

    siteTask.start_date = newStartDate
    this.saveSiteTask(siteTaskId, siteTask)
  }

  saveSiteTask(siteTaskId, siteTask) {
    updateSiteTask(siteTaskId, siteTask).then((remoteSiteTask) => {
      this.remoteData.calendarTasks = this.remoteData.calendarTasks.map((task) => task.id === siteTaskId ? remoteSiteTask : task)
      const { projectTaskList } = serializeCalendarData(this.remoteData)
      this.updateCalendarTaskList(projectTaskList)

      const existingEvent = scheduler.getEvent(remoteSiteTask.id)
      if (existingEvent) {
        Object.assign(existingEvent, serializeEvent(remoteSiteTask))
      } else {
        scheduler.addEvent(serializeEvent(remoteSiteTask))
      }
      scheduler.updateEvent(remoteSiteTask.id)

      addNotification('<span class="fa fa-cloud-arrow-up me-2"></span>' + i18next.t('command_result.saved'), 'bg-success', 'gantt-task-saved')
    })
  }

  updateCalendarTaskList(projectTaskList) {
    this.calendarTaskListerTarget.innerHTML = renderSites(projectTaskList)
    this.addDraggableEventListeners()
  }

  addDraggableEventListeners() {
    this.calendarTaskListerTarget.querySelectorAll('[data-draggable-event]').forEach(btn => {
      btn.addEventListener('dragstart', (e) => e.dataTransfer.setData('text/plain', btn.dataset.siteTaskId))
    })
  }

  showMinical() {
    scheduler.destroyCalendar()
    scheduler.renderCalendar({
      position: 'dhx_minical_icon',
      date: scheduler.getState().date,
      navigation: true,
      handler: function (date, calendar) {
        scheduler.setCurrentView(date);
        scheduler.destroyCalendar();
      }
    });
  }

  initializeCollapseState() {
    // Set default zoom level based on projectId presence
    if (this.projectIdValue) {
      this.expandToCategories()
    } else {
      this.collapseAll()
    }
  }

  collapseAll() {
    collapseAllUtils(this.calendarTaskListerTarget)
    setExpandCollapseActiveButton(this.element, 'collapseAll')
  }

  expandToCategories() {
    expandToCategoriesUtils(this.calendarTaskListerTarget)
    setExpandCollapseActiveButton(this.element, 'expandToCategories')
  }

  expandAll() {
    expandAllUtils(this.calendarTaskListerTarget)
    setExpandCollapseActiveButton(this.element, 'expandAll')
  }
}
